/**
 * A MasterService is a service that is run in the master app on every page. Other services can subscribe to information
 * provided by a MasterService and they will get callback notifications when the information is changed. It is meant for
 * things that are shared by every app on a page.
 *
 * MasterService stores its data in the window object because AngularJS services from different apps are not shared.
 * By using the window object, all apps can be notified properly.
 *
 * MasterService shouldn't be used directly but extended.
 */
class MasterService {
  constructor() {
    /**
     * Map with keys that can be subscribed to. The keys should be set by the extending class in its constructor.
     * Trying to subscribe to a key not in this map will error.
     *
     * Each value of the map should be an object {value, callbacks}.
     *
     * @type {Map}
     */
    if (window._masterservice_subscriptions == null) {
      window._masterservice_subscriptions = new Map();
    }
  }

  // Subscribe a callback to the given key.
  _subscribe(key, callback, priority=false) {
    if (!window._masterservice_subscriptions.has(key)) {
      throw new Error(`Unable to subscribe to unknown key ${key.toString()}.`);
    }

    const sub = window._masterservice_subscriptions.get(key);
    if (priority) {
      sub.callbacks.unshift(callback)
    }
    else {
      sub.callbacks.push(callback);
    }

    window._masterservice_subscriptions.set(key, sub);
    callback(sub.value);
  }

  // Unsubscribe the callback from the given key. The callback must be the same instance as the subscribed one, as they
  // are compared with identity equality.
  _unsubscribe(key, callback) {
    if (!window._masterservice_subscriptions.has(key)) {
      throw new Error(`Unable to unsubscribe from unknown key ${key.toString()}.`);
    }

    const sub = window._masterservice_subscriptions.get(key);

    // Only keep callbacks that don't match the given one
    sub.callbacks = sub.callbacks.filter(c => c !== callback);

    window._masterservice_subscriptions.set(key, sub);
  }

  // Set the value of the given key, notifying all registered callbacks.
  _setValue(key, value) {
    const sub = window._masterservice_subscriptions.get(key);
    sub.value = value;

    for (const callback of sub.callbacks) {
      callback(value);
    }
  }

  // Retrieve common value
  _getValue(key) {
    const sub = window._masterservice_subscriptions.get(key);
    return sub.value;
  }

  // Add the given key as a key that can be subscribed to. Unknown keys will trigger errors when using the API.
  _addKey(key) {
    // If the key has already been added, don't add it again
    if (window._masterservice_subscriptions.has(key)) {
      return;
    }

    window._masterservice_subscriptions.set(key, {
      value: null,
      callbacks: []
    });
  }
}

export default MasterService;



// WEBPACK FOOTER //
// ./src/app/common/master.service.js